import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from '../../../../libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from '../../../../helpers';
import UIHelper from '../../../../helpers/UIHelper';
import { Table } from 'antd';
import { fileNameEdit } from 'helpers/FileUtils';


import Text from 'antd/es/typography/Text';
import _ from 'lodash';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/tickets/');
const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData: [],
  barChart: [],
  barChangeLine: [],
  total: {},
};

const useInvoicesTickets = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
    },
    {
      title: t('Tickets YTD'),
      dataIndex: 'Tickets YTD',
      render: (data) => Helpers.numberWithCommas(data),
    },
    {
      title: t('Tickets LY'),
      dataIndex: 'Tickets LY',
      render: (data) => Helpers.numberWithCommas(data),
    },
    {
      title: t('Tickets vs LY (+/-%)'),
      dataIndex: 'Tickets vs LY (+/-%)',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza % Tickets YTD'),
      dataIndex: 'Incidenza % Tickets YTD',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza % Tickets LY'),
      dataIndex: 'Incidenza % Tickets LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let data, total;

        if (!isAllReports) {
          const {
            data: { data: backData, total: backTotal },
          } = await api.getFootfalls({ params });
          data = backData;
          total = backTotal;
        } else {
          const { data: backData, total: backTotal } = allReportsData;
          data = backData;
          total = backTotal;
        }

        if (!data) return;

        const tableKeys = Object.keys(total);
        const filteredColumns = columns.filter((column) =>
          tableKeys.includes(column.dataIndex),
        );
        const lineChartData1 = {
          id: 'Tickets LY',
          // color: 'blue',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Tickets LY'],
          })),
        };
        const lineChartData2 = {
          id: 'Tickets YTD',
          // color: 'blue',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Tickets YTD'],
          })),
        };

        const maxValueFroLy = _.maxBy(data, 'Incidenza % Tickets YTD')[
          'Incidenza % Tickets YTD'
        ];
        const maxValueFroYtd = _.maxBy(data, 'Incidenza % Tickets LY')[
          'Incidenza % Tickets LY'
        ];

        const maxValue =
          maxValueFroLy > maxValueFroYtd ? maxValueFroLy : maxValueFroYtd;

        const barChart = data.map((item) => ({
          date: item['Periodo'],
          'Tickets LY': item['Incidenza % Tickets LY'],
          'Tickets LYColor': '#61cdbb',
          'Tickets YTD': item['Incidenza % Tickets YTD'],
          'Tickets YTDColor': '#f47560',
        }));

        const barChangeLine = {
          id: '% Tickets VS LY',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Tickets vs LY (+/-%)'],
            tooltipKeys: [
              {
                key: 'Pezzi LY',
                value: Helpers.changeDotToComa(
                  format(item['Incidenza % Tickets LY']),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Pezzi YTD',
                value: Helpers.changeDotToComa(
                  format(item['Incidenza % Tickets YTD']),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: '% Tickets VS LY',
                value: Helpers.changeDotToComa(
                  format(item['Tickets vs LY (+/-%)']),
                ),
                color: '#1f77b4',
                percent: '',
              },
            ],
          })),
        };

        setData({
          tableData: data,
          lineChartData: [lineChartData1, lineChartData2],
          barChart,
          maxValue,
          barChangeLine: [barChangeLine],
          total,
        });
        setColumns(filteredColumns);
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          const event = new CustomEvent('status', { detail: 'ok' });

          setTimeout(() => {
            window.dispatchEvent(event);
          }, 5000);
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    columns,
    data,
    filters,
    format,
    title,
    loading,
    handleDownloadExcel,
    summary,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesTickets;
